/**
 * Thumbs up or down, with a reason, on one prospect (PRD §4.5) — and undo.
 *
 * The vote is sent, then the panel and the list are refreshed: a "remove from list" reason takes the
 * prospect off the list on the next scoring pass, which the server asks for, so the list notes that
 * change is coming rather than pretending it already happened.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { prospectingApi, queryKeys } from '@client/api/integrations';
import type { Prospecting } from '@client/api/types';
import { trackProspectFeedback } from '../lib/analytics';

export function useProspectFeedback(
  provider: Prospecting.Provider,
  detail: Prospecting.ProspectDetail | undefined,
  modelVersion: string,
) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (feedback: { vote: Prospecting.FeedbackVote; reasonCode: string | null }) =>
      prospectingApi.prospects.feedback(provider, detail!.prospectId, feedback),
    onSuccess: (_result, feedback) => {
      trackProspectFeedback(feedback.vote, feedback.reasonCode, detail!.tier, modelVersion);
      queryClient.invalidateQueries({ queryKey: queryKeys.prospecting.prospect(provider, detail!.prospectId) });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.prospecting.all, provider, 'prospects'] });
    },
  });

  return {
    vote: (vote: 'up' | 'down', reasonCode: string) => mutation.mutate({ vote, reasonCode }),
    undo: () => mutation.mutate({ vote: 'cleared', reasonCode: null }),
    isSaving: mutation.isPending,
    error: mutation.error,
  };
}
